import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { useAuth } from "@/app/providers/useAuth";
import { propertyService } from "@/features/properties/model/propertyServices";
import type { PropertyApiResponse } from "@/features/properties/model/propertiesTypes";
import type { AddressSelectOption } from "./AddressSelectPill.types";
import { getDeviceId } from "@/features/properties/model/guestSession";
import { tokenService } from "@/services/auth/token.service";

export interface UseAddressListViewModelResult {
  options: AddressSelectOption[];
  properties: PropertyApiResponse[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

const buildPropertiesQueryParam = (params: {
  account_id?: string;
  device_id?: string;
  ip_address?: string;
}): string => {
  const sp = new URLSearchParams();
  if (params.account_id) sp.set("account_id", params.account_id);
  if (params.device_id) sp.set("device_id", params.device_id);
  if (params.ip_address) sp.set("ip_address", params.ip_address);
  return sp.toString();
};

const mapPropertiesToOptions = (properties: PropertyApiResponse[]): AddressSelectOption[] => {
  const mapped = properties
    .map((p) => ({
      id: p?.property_id || "",
      label: p?.address?.full_address || p?.address?.street || "",
    }))
    .filter((p): p is { id: string; label: string } =>
      Boolean(p.id && p.id.trim() && p.label && p.label.trim())
    )
    .map((p) => ({ value: p.id, label: p.label }));

  // Dedupe while preserving order
  const seen = new Set<string>();
  const deduped: AddressSelectOption[] = [];
  for (const opt of mapped) {
    if (seen.has(opt.value)) continue;
    seen.add(opt.value);
    deduped.push(opt);
  }

  return deduped;
};

/**
 * Address list ViewModel
 * Fetches the user's properties and adapts them to `AddressSelectPill` options.
 * Uses account_id if user is present; otherwise falls back to device_id.
 */
export const useAddressListViewModel = (): UseAddressListViewModelResult => {
  const { user } = useAuth();
  const storedUser = tokenService.getUser();
  const activeUser = user || storedUser;

  const accountId = activeUser?.id ? String(activeUser.id) : undefined;
  const deviceId = !accountId ? getDeviceId() : undefined;

  const queryParam = useMemo(() => {
    // Note: ip_address is intentionally omitted for now.
    return buildPropertiesQueryParam({
      account_id: accountId,
      device_id: deviceId,
    });
  }, [accountId, deviceId]);

  const queryKeyId = accountId ? `account_${accountId}` : `device_${deviceId}`;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [QUERY_KEYS.PROPERTIES, queryKeyId],
    queryFn: () => propertyService.getAll(queryParam),
    enabled: Boolean(accountId || deviceId),
  });

  const options = useMemo(() => mapPropertiesToOptions(data || []), [data]);

  return {
    options,
    properties: data || [],
    isLoading,
    error: (error as Error) || null,
    refetch,
  };
};
